import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { ENTITIES, buildFolderPath } from "../../services/contractService";

// ─── CSV helpers ────────────────────────────────────────────────────────────

const COLUMNS = [
  "RefNo","Title","Entity","VendorName","ContractType","Department",
  "CategoryManager","ContractOwner",
  "EffectiveDate","ExpiryDate","Duration","IsRenewal",
  "Status","CeilingValueMonthly","CeilingValueAnnual",
  "DeviationFromPayment","RequestDate","Comments","ParentContractRef",
];
const REQUIRED = ["RefNo","Title","Entity","VendorName","ContractType","Department","EffectiveDate"];

const SAMPLE_ROW = [
  "LEK/276/26-01","Service Agreement – XYZ Ltd","LEKOIL 276 LIMITED","XYZ Limited",
  "Service","SCM","John Doe","Jane Smith","Mike Brown",
  "2026-01-15","2027-01-14","12 months","Yes",
  "Executed","50000","600000","No","2026-01-10","Existing contract import","",
];

function parseCSV(text) {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  return lines.map(line => {
    const row = [];
    let cur = "", inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (ch === "," && !inQ) { row.push(cur.trim()); cur = ""; }
      else cur += ch;
    }
    row.push(cur.trim());
    return row;
  });
}

function downloadTemplate(contractTypes) {
  const header = COLUMNS.join(",");
  const notes = [
    `# Required: ${REQUIRED.join(", ")}`,
    `# Entity must be one of: ${ENTITIES.join(" | ")}`,
    `# ContractType must be one of: ${contractTypes.join(" | ")}`,
    `# IsRenewal / DeviationFromPayment: Yes or No`,
    `# Dates format: YYYY-MM-DD`,
    `# Status: leave blank to default to Executed`,
    `# ParentContractRef: fill only for Addendum contracts`,
    `# DELETE these note rows before uploading`,
  ].join("\n");
  const sample = SAMPLE_ROW.map(v => v.includes(",") ? `"${v}"` : v).join(",");
  const csv = `${notes}\n${header}\n${sample}\n`;
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "LexCore_BulkImport_Template.csv";
  a.click(); URL.revokeObjectURL(url);
}

function validateRow(row, contractTypes) {
  const errs = [];
  REQUIRED.forEach(col => { if (!row[col]) errs.push(`${col} is required`); });
  if (row.Entity && !ENTITIES.includes(row.Entity))
    errs.push(`Entity "${row.Entity}" is not valid`);
  if (row.ContractType && !contractTypes.includes(row.ContractType))
    errs.push(`ContractType "${row.ContractType}" is not valid`);
  if (row.EffectiveDate && isNaN(Date.parse(row.EffectiveDate)))
    errs.push("EffectiveDate must be YYYY-MM-DD");
  if (row.ExpiryDate && isNaN(Date.parse(row.ExpiryDate)))
    errs.push("ExpiryDate must be YYYY-MM-DD");
  return errs;
}

// ─── Utility Classes ─────────────────────────────────────────────────────────
const getCardClass = () => "bg-blue-50 border border-gray-200 rounded-lg p-6";
const getBtnClass = (gold) => gold
  ? "px-5.5 py-2.5 border-none rounded-lg cursor-pointer font-sans text-sm font-semibold bg-blue-600 text-white hover:bg-blue-500"
  : "px-5.5 py-2.5 border-none rounded-lg cursor-pointer font-sans text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200";
const getThClass = () => "px-3.5 py-2.5 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-white border-b border-gray-200 whitespace-nowrap";
const getTdClass = () => "px-3.5 py-2 text-sm border-b border-gray-200 text-gray-700 max-w-sm overflow-hidden text-ellipsis whitespace-nowrap";

// ─── Component ────────────────────────────────────────────────────────────────
export const BulkImport = () => {
  const { service, addNotification, currentUser, contractTypes } = useApp();
  const navigate = useNavigate();

  const [stage, setStage] = useState("upload"); // upload | preview | importing | done
  const [drag, setDrag] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowErrors, setRowErrors] = useState({});
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: [] });

  // ── Parse uploaded file ───────────────────────────────────────────────────
  const processFile = (file) => {
    if (!file.name.endsWith(".csv")) {
      addNotification("Please upload a CSV file (.csv)", "error"); return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      // Strip comment/note rows (starting with #) and blank rows
      const clean = parsed.filter(r => r.length > 1 && !r[0].startsWith("#") && r.some(v => v));
      if (clean.length < 2) { addNotification("File has no data rows", "error"); return; }

      const headers = clean[0].map(h => h.trim());
      const missing = COLUMNS.filter(c => !headers.includes(c));
      if (missing.length) {
        addNotification(`Missing columns: ${missing.join(", ")}`, "error"); return;
      }

      const data = clean.slice(1).map(r => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (r[i] || "").trim(); });
        return obj;
      }).filter(r => Object.values(r).some(v => v));

      const errs = {};
      data.forEach((row, i) => {
        const e = validateRow(row, contractTypes);
        if (e.length) errs[i] = e;
      });

      setRows(data);
      setRowErrors(errs);
      setStage("preview");
    };
    reader.readAsText(file);
  };

  // ── Import ────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    const validRows = rows.filter((_, i) => !rowErrors[i]);
    setProgress({ done: 0, total: validRows.length, failed: [] });
    setStage("importing");

    // Fetch drive once for all rows
    let driveId = null;
    try {
      const driveData = await service.getDrives();
      const drive = driveData?.value?.find(d => d.name === "ContractDocuments");
      if (drive) driveId = drive.id;
    } catch(_) {}

    const failed = [];
    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      try {
        const effDate = row.EffectiveDate || new Date().toISOString().slice(0, 10);
        const year = new Date(effDate).getFullYear();
        const month = new Date(effDate).getMonth() + 1;

        const fields = {
          Title: row.Title,
          RefNo: row.RefNo,
          Entity: row.Entity,
          VendorName: row.VendorName,
          ContractType: row.ContractType,
          Department: row.Department,
          CategoryManager: row.CategoryManager || "",
          ContractOwner: row.ContractOwner || "",
          EffectiveDate: row.EffectiveDate || null,
          ExpiryDate: row.ExpiryDate || null,
          Duration: row.Duration || "",
          IsRenewal: row.IsRenewal || "No",
          Status: row.Status || "Executed",
          CeilingValueMonthly: row.CeilingValueMonthly || "",
          CeilingValueAnnual: row.CeilingValueAnnual || "",
          DeviationFromPayment: row.DeviationFromPayment || "No",
          Comments: row.Comments || "",
          RequestDate: row.RequestDate || effDate,
          RequestedBy: currentUser?.mail || "",
          Year: String(year),
          Month: String(month),
          ...(row.ParentContractRef ? { ParentContractRef: row.ParentContractRef } : {}),
        };

        await service.createListItem("Contracts", fields);

        // Create folder so files can be uploaded later
        if (driveId) {
          const folderPath = buildFolderPath(row.Entity, row.Department, year, month, row.RefNo);
          const parts = folderPath.split("/");
          let cur = "";
          for (const part of parts) {
            try { await service.createFolder(driveId, cur || null, part); } catch(_) {}
            cur = cur ? `${cur}/${part}` : part;
          }
        }
      } catch (ex) {
        failed.push({ ref: row.RefNo, error: ex.message });
      }
      setProgress(p => ({ done: p.done + 1, total: p.total, failed: [...p.failed, ...( failed.length > p.failed.length ? [failed[failed.length-1]] : [])] }));
    }
    setStage("done");
  };

  const errorCount = Object.keys(rowErrors).length;
  const validCount = rows.length - errorCount;
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-medium text-gray-900 mb-1">
          Bulk Contract Import
        </h1>
        <p className="text-gray-700 m-0">
          Upload existing contracts from a CSV file. Files can be added to each contract afterwards.
        </p>
        <div className="h-0.5 bg-gradient-to-r from-blue-600 to-transparent mt-2" />
      </div>

      {/* Step indicators */}
      <div className="flex gap-0 mb-7 items-center">
        {[["1","Upload CSV"],["2","Preview & Validate"],["3","Import"],["4","Done"]].map(([n,label],i)=>{
          const stageIdx = {upload:0,preview:1,importing:2,done:3}[stage];
          const active = i === stageIdx, done = i < stageIdx;
          return (
            <React.Fragment key={n}>
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  done ? "bg-blue-600 text-white" : active ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"
                }`}>
                  {done ? "✓" : n}
                </div>
                <span className={`text-xs ${
                  active ? "text-gray-900 font-semibold" : done ? "text-blue-600" : "text-gray-700"
                }`}>{label}</span>
              </div>
              {i < 3 && <div className="flex-1 h-px bg-gray-100 mx-3" />}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── STAGE: UPLOAD ── */}
      {stage === "upload" && (
        <div className="flex flex-col gap-5">
          {/* Template download */}
          <div className={getCardClass()}>
            <div className="font-serif text-lg font-medium text-blue-600 mb-3">
              Step 1 — Download the template
            </div>
            <p className="text-gray-700 text-sm mb-4 leading-relaxed">
              Download the CSV template, fill in your contract data in Excel or any spreadsheet app,
              then save as <strong className="text-gray-900">CSV (Comma delimited)</strong> before uploading.
              The first rows starting with <code className="text-blue-600">#</code> are notes — delete them before uploading.
            </p>
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
              <div className="text-xs text-gray-700 mb-2 font-semibold">REQUIRED COLUMNS</div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {REQUIRED.map(c=>(
                  <span key={c} className="px-2.5 py-1 rounded-full bg-blue-600/20 text-blue-600 text-xs font-medium">{c}</span>
                ))}
              </div>
              <div className="text-xs text-gray-700 mt-3 mb-2 font-semibold">VALID ENTITY VALUES</div>
              <div className="flex flex-col gap-0.5 mb-4">
                {ENTITIES.map(e=>(
                  <span key={e} className="text-xs text-gray-700 font-mono">{e}</span>
                ))}
              </div>
              <div className="text-xs text-gray-700 mt-3 mb-2 font-semibold">VALID CONTRACT TYPES</div>
              <div className="flex flex-wrap gap-1.5">
                {contractTypes.map(t=>(
                  <span key={t} className="px-2.5 py-1 rounded-full bg-blue-600/20 text-blue-700 text-xs">{t}</span>
                ))}
              </div>
            </div>
            <button onClick={()=>downloadTemplate(contractTypes)} className={getBtnClass(true)}>
              ⬇ Download CSV Template
            </button>
          </div>

          {/* File upload */}
          <div className={getCardClass()}>
            <div className="font-serif text-lg font-medium text-blue-600 mb-3">
              Step 2 — Upload your filled CSV
            </div>
            <div
              onDragOver={e=>{e.preventDefault();setDrag(true);}}
              onDragEnter={e=>{e.preventDefault();setDrag(true);}}
              onDragLeave={e=>{e.preventDefault();setDrag(false);}}
              onDrop={e=>{e.preventDefault();setDrag(false);if(e.dataTransfer.files[0]) processFile(e.dataTransfer.files[0]);}}
              onClick={()=>document.getElementById("bulkCsvInput").click()}
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-all duration-200 ${
                drag ? "border-blue-600 bg-blue-600/5" : "border-gray-200 bg-transparent"
              }`}
            >
              <input id="bulkCsvInput" type="file" accept=".csv" className="hidden"
                onChange={e=>{if(e.target.files[0]) processFile(e.target.files[0]);}}/>
              <div className="text-3xl mb-2.5 pointer-events-none">📂</div>
              <div className={`text-base mb-1 pointer-events-none ${drag ? "text-blue-600" : "text-gray-700"}`}>
                {drag?"Drop your CSV here":"Click to browse or drag your CSV file here"}
              </div>
              <div className="text-xs text-gray-700 pointer-events-none">Only .csv files accepted</div>
            </div>
          </div>
        </div>
      )}

      {/* ── STAGE: PREVIEW ── */}
      {stage === "preview" && (
        <div className="flex flex-col gap-4">
          {/* Summary bar */}
          <div className="flex gap-3 flex-wrap">
            <div className="bg-blue-50 border border-gray-200 rounded-lg p-3.5 flex-1 min-w-40">
              <div className="text-2xl font-bold text-gray-900">{rows.length}</div>
              <div className="text-sm text-gray-700">Rows detected</div>
            </div>
            <div className={`bg-blue-50 border rounded-lg p-3.5 flex-1 min-w-40 ${
              validCount ? "border-green-300" : "border-gray-200"
            }`}>
              <div className="text-2xl font-bold text-green-700">{validCount}</div>
              <div className="text-sm text-gray-700">Valid — ready to import</div>
            </div>
            <div className={`bg-blue-50 border rounded-lg p-3.5 flex-1 min-w-40 ${
              errorCount ? "border-red-300" : "border-gray-200"
            }`}>
              <div className={`text-2xl font-bold ${errorCount ? "text-red-700" : "text-gray-700"}`}>{errorCount}</div>
              <div className="text-sm text-gray-700">Rows with errors (skipped)</div>
            </div>
          </div>

          {errorCount > 0 && (
            <div className="bg-blue-50 border border-red-300 rounded-lg p-6">
              <div className="text-sm font-semibold text-red-700 mb-2.5">Validation errors — these rows will be skipped</div>
              <div className="flex flex-col gap-1.5 max-h-50 overflow-y-auto">
                {Object.entries(rowErrors).map(([i,errs])=>(
                  <div key={i} className="text-sm text-gray-700">
                    <span className="text-blue-600 font-mono">Row {Number(i)+2}</span>
                    {" — "}{rows[i]?.RefNo || "no ref"}
                    {": "}<span className="text-red-700">{errs.join("; ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data preview table */}
          <div className="bg-blue-50 border border-gray-200 rounded-lg p-0 overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-200 flex justify-between items-center">
              <div className="text-base font-serif font-medium text-gray-900">
                Data Preview
              </div>
              <span className="text-xs text-gray-700">Showing all {rows.length} rows</span>
            </div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th className={getThClass()}>#</th>
                    {["RefNo","Title","Entity","ContractType","VendorName","Department","EffectiveDate","Status"].map(c=>(
                      <th key={c} className={getThClass()}>{c}</th>
                    ))}
                    <th className={getThClass()}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row,i)=>{
                    const hasErr = !!rowErrors[i];
                    return (
                      <tr key={i} className={hasErr ? "bg-red-50" : i%2===0 ? "bg-transparent" : "bg-white"}>
                        <td className={`${getTdClass()} text-gray-700 w-9`}>{i+2}</td>
                        {["RefNo","Title","Entity","ContractType","VendorName","Department","EffectiveDate","Status"].map(c=>(
                          <td
                            key={c}
                            className={`${getTdClass()} ${c==="RefNo" ? "font-mono font-bold text-gray-900" : ""}`}
                            title={row[c]}
                          >
                            {row[c]||"—"}
                          </td>
                        ))}
                        <td className={getTdClass()}>
                          {hasErr
                            ? <span className="text-red-700 text-xs">⚠ {rowErrors[i][0]}</span>
                            : <span className="text-green-700 text-xs">✓ Valid</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <button onClick={()=>setStage("upload")} className={getBtnClass(false)}>← Re-upload</button>
            <button onClick={handleImport} disabled={validCount===0} className={getBtnClass(validCount>0)}>
              Import {validCount} Contract{validCount!==1?"s":""}
            </button>
          </div>
        </div>
      )}

      {/* ── STAGE: IMPORTING ── */}
      {stage === "importing" && (
        <div className="bg-blue-50 border border-gray-200 rounded-lg p-12 text-center">
          <div className="w-14 h-14 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-6" />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div className="font-serif text-2xl text-gray-900 mb-2">
            Importing contracts…
          </div>
          <div className="text-sm text-gray-700 mb-6">
            {progress.done} of {progress.total} processed — please keep this page open
          </div>
          <div className="bg-white rounded-full h-2.5 overflow-hidden max-w-sm mx-auto">
            <div className="h-full bg-gradient-to-r from-blue-600 to-blue-500 rounded-full transition-all duration-300" style={{width:`${pct}%`}} />
          </div>
          <div className="text-sm text-blue-600 mt-2.5 font-mono">{pct}%</div>
        </div>
      )}

      {/* ── STAGE: DONE ── */}
      {stage === "done" && (
        <div className="flex flex-col gap-4">
          <div className="bg-blue-50 border border-gray-200 rounded-lg p-10 text-center">
            <div className="text-5xl mb-3">
              {progress.failed.length === 0 ? "✅" : "⚠️"}
            </div>
            <div className="font-serif text-2xl text-gray-900 mb-2">
              Import Complete
            </div>
            <div className="flex gap-4 justify-center flex-wrap mt-4">
              <div className="px-6 py-3 bg-green-50 border border-green-300 rounded-lg">
                <div className="text-2xl font-bold text-green-700">{progress.total - progress.failed.length}</div>
                <div className="text-sm text-gray-700">Successfully imported</div>
              </div>
              {progress.failed.length > 0 && (
                <div className="px-6 py-3 bg-red-50 border border-red-300 rounded-lg">
                  <div className="text-2xl font-bold text-red-700">{progress.failed.length}</div>
                  <div className="text-sm text-gray-700">Failed</div>
                </div>
              )}
            </div>
          </div>

          {progress.failed.length > 0 && (
            <div className="bg-blue-50 border border-red-300 rounded-lg p-6">
              <div className="text-sm font-semibold text-red-700 mb-3">Failed imports</div>
              <div className="flex flex-col gap-1.5">
                {progress.failed.map((f,i)=>(
                  <div key={i} className="text-sm flex gap-3 items-start">
                    <span className="text-blue-600 font-mono flex-shrink-0">{f.ref}</span>
                    <span className="text-red-700">{f.error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              onClick={()=>{setStage("upload");setRows([]);setRowErrors({});setProgress({done:0,total:0,failed:[]});}}
              className={getBtnClass(false)}
            >
              Import Another File
            </button>
            <button onClick={()=>navigate("/contracts")} className={getBtnClass(true)}>
              View Contracts →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
